import { useReadContract } from "wagmi";
import { EXPECTED_CHAIN_ID } from "@/configs/expected-chain";
import { ELECTION_ABI } from "@/contract/abi/election";
import ContractAddress from "@/contract/addresses";
import { $api } from "@/lib/api-client";
import type { components } from "@/types/api/api-types";
import type { MergedCandidate } from "@/types/dao/election";

// OpenAPI-derived type — single source of truth for the GET /election row shape.
// Replaces the legacy fixture `ElectionRow` (over-narrow `wallet_address: 0x${string}`
// can't be expressed in OpenAPI 3.0).
//
// 2026-05-23 backend trim: list response is now {id, wallet_address, cycle,
// nickname}. name/email collapsed server-side into the nickname resolver, so
// the frontend just reads the resolved nickname and lets ElectionRow's
// fallback chain handle the empty case.
type ElectionRow = components["schemas"]["ElectionResponseDto"];

function nonEmptyString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value : null;
}

/**
 * Fetches candidacy data for a given cycle.
 *
 * Merges on-chain candidate list (authoritative) with backend metadata rows.
 * - Chain is authoritative: only candidates on-chain are returned.
 * - Backend-only rows are excluded (EC-P1-5).
 * - Chain-only candidates get nickname=null (race window — backend row not yet inserted).
 * - If backend errors but chain ok, degrades gracefully (EC-G4).
 *
 * r5 S3: maps backend nickname → MergedCandidate.nickname
 * r5 S4: drops lossy Number() coercion; sentinel-subtract:
 *        totalReceivedVoteDnmWei = vote_count > 0n ? vote_count - 1n : 0n
 * CHG-02 sort: results sorted by totalReceivedVoteDnmWei DESC. Applies in
 *        both candidate-cycle (all 0 → stable chain order) and voting-cycle
 *        (dynamic re-rank as votes accumulate).
 */
export function useFetchCandidates(cycle: number | undefined) {
  const {
    data: chainCandidates,
    isLoading: loadingChain,
    isError: errorChain,
    refetch,
  } = useReadContract({
    address: ContractAddress.ELECTION,
    abi: ELECTION_ABI,
    chainId: EXPECTED_CHAIN_ID,
    functionName: "getCycleCandidates",
    args: [BigInt(cycle ?? 0)],
    query: {
      enabled: cycle !== undefined,
      retry: false,
    },
  });

  // S2 fix — gate the backend query on cycle being known. Previously it fired
  // immediately with `cycle: undefined`, which the API serializes as either
  // omission or cycle=0 and returns wrong-cycle candidates that flash for one
  // paint before the real cycle resolves and refetches. Mirrors the cycle gate
  // we use on the chain query (line above) and the pattern in ActsTab.
  const backendQuery = $api.useQuery(
    "get",
    "/election",
    { params: { query: { cycle } } },
    { enabled: cycle !== undefined },
  );
  const {
    data: backendData,
    isLoading: loadingBackend,
    isError: errorBackend,
  } = backendQuery;

  // Schema is wrapped via WrappedApiResponseDto on the backend; openapi types
  // reflect that shape directly so `backendData.data` is the typed array.
  const backendRows: ElectionRow[] = backendData?.data ?? [];

  let data: MergedCandidate[] | undefined;

  if (!loadingChain && !errorChain && chainCandidates !== undefined) {
    // Build lookup map of backend rows by lowercased wallet address
    const backendByWallet = new Map<string, ElectionRow>();
    if (!errorBackend) {
      for (const row of backendRows) {
        backendByWallet.set(row.wallet_address.toLowerCase(), row);
      }
    }

    // Chain is authoritative: map over on-chain entries only
    data = chainCandidates
      .map((c) => {
        // c.addr is `0x${string}` from viem ABI decode; toLowerCase() preserves prefix
        const addrSuffix = c.addr.slice(2).toLowerCase();
        const addr: `0x${string}` = `0x${addrSuffix}`;
        const backendRow = backendByWallet.get(addr);

        // S4 sentinel-subtract: Election.sol:150 sets vote_count = 1n at registration.
        // True received-vote DNM in wei = vote_count > 0n ? vote_count - 1n : 0n.
        const totalReceivedVoteDnmWei =
          c.vote_count > 0n ? c.vote_count - 1n : 0n;

        return {
          walletAddress: addr,
          // S3: nickname from backend; null only during chain-only race
          // window. Backend already resolves election.name → users.nickname →
          // legacy_display_name → email → wallet_address server-side, so this
          // single field carries the full fallback chain.
          nickname: nonEmptyString(backendRow?.nickname),
          totalReceivedVoteDnmWei,
          registrationId: backendRow?.id ?? null,
        };
      })
      // CHG-02: sort by received DNM DESC. Stable for equal values (preserves
      // chain registration order during candidate-cycle when all votes are 0).
      .sort((a, b) => {
        if (b.totalReceivedVoteDnmWei > a.totalReceivedVoteDnmWei) return 1;
        if (b.totalReceivedVoteDnmWei < a.totalReceivedVoteDnmWei) return -1;
        return 0;
      });
  }

  return {
    data,
    isLoading: loadingChain || loadingBackend,
    isError: errorChain,
    refetch,
  };
}
